<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import type { AdminPersonRecord } from '../../lib/types';
  import { updateAdminPerson } from '../../lib/convexApi';

  export let open = false;
  export let person: AdminPersonRecord | null = null;

  const dispatch = createEventDispatcher<{ close: void; saved: void }>();

  let name = '';
  let email = '';
  let phone = '';
  let saving = false;
  let error: string | null = null;

  let lastSnapshot = '';

  $: {
    const snapshot = open && person
      ? `${person.person.id}|${person.person.name}|${person.person.email ?? ''}|${person.person.phone ?? ''}`
      : '';
    if (snapshot !== lastSnapshot) {
      lastSnapshot = snapshot;
      if (open && person) {
        name = person.person.name;
        email = person.person.email ?? '';
        phone = person.person.phone ?? '';
        error = null;
      }
    }
  }

  $: emailLocked = person?.person.externalId ? true : false;

  function close() {
    dispatch('close');
  }

  async function save() {
    if (!person) return;
    if (!name.trim()) {
      error = 'Name is required';
      return;
    }
    saving = true;
    error = null;
    try {
      await updateAdminPerson(person.person.id, {
        name: name.trim(),
        email: emailLocked ? person.person.email ?? undefined : email.trim() || undefined,
        phone: phone.trim() || undefined
      });
      dispatch('saved');
      close();
    } catch (err) {
      error = err instanceof Error ? err.message : 'Failed to update person';
    } finally {
      saving = false;
    }
  }
</script>

{#if open && person}
  <dialog class="person-editor" open>
    <div class="modal-card">
      <header>
        <div>
          <h3>Edit person</h3>
          {#if person.person.externalId}
            <p class="muted">Linked to Clerk user {person.person.externalId}</p>
          {/if}
        </div>
        <button type="button" class="ghost" on:click={close}>Close</button>
      </header>

      <form on:submit|preventDefault={save}>
        <label>
          <span>Name</span>
          <input type="text" bind:value={name} required />
        </label>
        <label class:disabled={emailLocked}>
          <span>Email {emailLocked ? '(managed by Clerk)' : ''}</span>
          <input type="email" bind:value={email} disabled={emailLocked} />
        </label>
        <label>
          <span>Phone</span>
          <input type="text" bind:value={phone} placeholder="+1..." />
        </label>

        {#if error}
          <p class="error">{error}</p>
        {/if}

        <footer>
          <button type="button" class="ghost" on:click={close}>Cancel</button>
          <button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
        </footer>
      </form>
    </div>
  </dialog>
{/if}

<style>
  dialog.person-editor {
    position: fixed;
    inset: 0;
    margin: auto;
    width: min(480px, 90vw);
    border: none;
    background: rgba(7, 8, 12, 0.75);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
  }

  .modal-card {
    width: 100%;
    background: linear-gradient(180deg, rgba(16, 18, 26, 0.92), rgba(16, 18, 26, 0.85));
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: var(--radius-xl);
    padding: var(--space-5);
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }

  header {
    display: flex;
    justify-content: space-between;
    gap: var(--space-3);
  }

  header h3 {
    margin: 0;
  }

  form {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  label {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    font-size: 0.85rem;
  }

  input {
    padding: 0.6rem 0.75rem;
    border-radius: var(--radius-md);
    border: 1px solid transparent;
    background: rgba(255, 255, 255, 0.08);
    color: inherit;
  }

  label.disabled input {
    opacity: 0.6;
    cursor: not-allowed;
  }

  footer {
    display: flex;
    justify-content: flex-end;
    gap: var(--space-3);
  }

  button {
    border: none;
    border-radius: var(--radius-lg);
    padding: 0.55rem 1rem;
    cursor: pointer;
    font-weight: 600;
  }

  button.ghost {
    background: rgba(255, 255, 255, 0.12);
    color: inherit;
  }

  button:not(.ghost) {
    background: linear-gradient(135deg, rgba(240, 169, 74, 0.6), rgba(178, 127, 244, 0.4));
    color: #050608;
  }

  button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .error {
    color: #ff9a9a;
    margin: 0;
  }
</style>
